import React, { useEffect, useRef, useState } from "react";

import Input from "../Input";

interface ConfirmationCodeProps {
  length?: number;
  value?: string;
  disabled?: boolean;
  onChange: (code: string) => void;
}

const ConfirmationCode = ({ length = 6, value, disabled, onChange }: ConfirmationCodeProps) => {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(""));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (value === undefined) return;
    if (value === digits.join("")) return;

    setDigits(Array.from({ length }, (_, index) => value[index] ?? ""));
  }, [value, length]);

  const commit = (nextDigits: string[]) => {
    setDigits(nextDigits);
    onChange(nextDigits.join(""));
  };

  const handleChange = (rawValue: string, index: number) => {
    const digit = rawValue.replace(/\D/g, "").slice(-1);

    const nextDigits = [...digits];
    nextDigits[index] = digit;
    commit(nextDigits);

    if (digit && index < length - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;

    const nextDigits = [...digits];
    pasted.split("").forEach((digit, offset) => {
      if (index + offset < length) nextDigits[index + offset] = digit;
    });
    commit(nextDigits);

    const focusIndex = Math.min(index + pasted.length, length - 1);
    inputs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex gap-2 w-full" role="group" aria-label="Código de verificação">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={(el) => {
            inputs.current[index] = el;
          }}
          className="w-full h-[61px] p-0 text-center font-bold text-[#7F98CC] bg-[#E6EAF6] border-2 border-[#E6EAF6] rounded-lg focus:border-[#213F93] focus:bg-white focus:text-[#213F93] disabled:bg-[#E6EAF6] disabled:opacity-50"
          type="text"
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          aria-label={`Dígito ${index + 1} de ${length}`}
          onChange={(event) => handleChange(event.target.value, index)}
          onKeyDown={(event) => handleKeyDown(event, index)}
          onPaste={(event) => handlePaste(event, index)}
        />
      ))}
    </div>
  );
};

export default ConfirmationCode;
